/**
 * Разбор поля `rubrics` из профиля участника.
 *
 * Интересы человек выбирает в своей визитке (дополнительное поле мероприятия
 * `extend_data`), а сервер отдаёт выбранное в карточке участника — массивом
 * групп с подписями на двух языках:
 *
 *   [{ label_ru, label_en, values: [{ id, label_ru, label_en, values: [] }] }]
 *
 * Тот же формат у рубрик экспонента, только там дерево до трёх уровней, а у
 * участника уровень один. Требования к серверу — в `docs/profile-rubrics.md`.
 *
 * Не миксин, обычный модуль с функциями — как businessCardFields.js рядом.
 */

/**
 * Знаки, которыми организатор отделяет тему от уточнения:
 * «Трансграничные платежи — маршруты, партнёры, комплаенс».
 *
 * Тире длинное (U+2014), короткое (U+2013) и математический минус (U+2212) —
 * на глаз они не различаются, а в справочник попадает то, что подставила
 * автозамена редактора. На этом мероприятии везде длинное.
 *
 * Обычного дефиса здесь нет намеренно: в справочнике стенда есть значение
 * «Компания - интегратор» — это одно понятие с лишними пробелами, а не тема с
 * уточнением, и делить его нельзя. По той же причине нет точки («И.Ткань»,
 * «т.д.», «Pav.No.2») и запятой.
 *
 * Новый знак добавляется правкой этой строки.
 */
const DASHES = '—–−';

/**
 * Тире — с пробелом с обеих сторон, двоеточие — хотя бы после: так знак внутри
 * слова не сработает. Ленивая первая группа отдаёт разбор самому левому знаку.
 */
const SPLIT_RE = new RegExp(`^(.+?)(?:\\s[${DASHES}]\\s|:\\s)(.+)$`);

/**
 * Подпись на языке интерфейса. Сервер шлёт обе независимо от языка в адресе,
 * поэтому при пустой берём вторую — пустой строки на экране быть не должно.
 */
function pickLabel(source, lang) {
  if (!source || typeof source !== 'object') return '';
  return source[`label_${lang}`] || source.label_ru || source.label_en || '';
}

/**
 * Делит подпись на тему и уточнение.
 * Знака нет — вся строка уходит в `head`, `tail` пустой.
 */
export function splitLabel(label) {
  const parts = SPLIT_RE.exec(label);
  if (!parts) return { head: label, tail: '' };
  return { head: parts[1].trim(), tail: parts[2].trim() };
}

/**
 * Собирает значения группы в плоский список.
 *
 * У участника вложенности нет — `values` у значения всегда пустой. Обход
 * рекурсивный на случай, если сервер когда-нибудь пришлёт сюда дерево, как у
 * экспонентов: вложенные значения встанут следом за родителем, а не пропадут.
 */
function flatValues(values, lang) {
  if (!Array.isArray(values)) return [];
  return values.reduce((acc, value) => {
    if (!value || typeof value !== 'object') return acc;
    const label = pickLabel(value, lang);
    if (label) acc.push({ id: value.id, ...splitLabel(label) });
    return acc.concat(flatValues(value.values, lang));
  }, []);
}

/**
 * Значения с уточнением — вперёд, короткие — следом. Внутри каждой части
 * порядок сервера сохраняется.
 *
 * Это нужно вёрстке. Значение с уточнением занимает строку целиком, поэтому
 * вперемешку с короткими оно то и дело оставляет соседа одного в строке, а
 * одинокая короткая плашка растягивается во всю ширину и выглядит как
 * оборванный заголовок. Сплошным блоком короткие набираются по несколько в
 * строку, и одинокой может остаться только последняя — а её растягивание
 * выключено распоркой.
 */
function splitFirst(values) {
  return [...values.filter((v) => v.tail), ...values.filter((v) => !v.tail)];
}

/**
 * Разбирает `rubrics` из ответа сервера в модель для экрана:
 * `[{ label, values: [{ id, head, tail }] }]`.
 *
 * Группы без значений отбрасываем — заголовок без содержимого показывать не за
 * чем. Пришло не то (нет ключа, пустой массив, чужой тип) — вернётся пустой
 * массив, и блока на экране не будет.
 */
export function parseRubrics(rubrics, lang = 'ru') {
  if (!Array.isArray(rubrics)) return [];
  return rubrics
    .filter((group) => group && typeof group === 'object')
    .map((group) => ({
      label: pickLabel(group, lang),
      values: splitFirst(flatValues(group.values, lang)),
    }))
    .filter((group) => group.values.length);
}

export default { parseRubrics, splitLabel };
